/*
 * Contains window level global functions. These are usually helper functions that can be used anywhere.
 * 
 * Developer notes. Be sure to document the usage of any function to be added here.
 * 
 * ====================================================================================================
 */

/**
 * Given a <script type="text/plain" data-indicator>...</script> in your markup, this function will 
 * take the contents of that script tag and return it as a value. This is useful for getting data
 * from liquid objects.
 * 
 * Tip, use the appropriate MIME type: text/plain (Nothing specific for numbers, so just use text/plain in general)
 * 
 * Example:
 * <script type="text/plain" data-something-configurable>{{ section.settings.configurable_thing }}</script>
 * ... in JS:
 * const configurableThing = getValueOrFail("data-something-configurable");
 * 
 * @param {String} dataAttr 
 * @returns {String} String value of the target.
 */
window.getValueOrFail = (dataAttr) => {
    const script = document.querySelector(`script[${dataAttr}]`);
    if (!script) {
      throw new Error(`Product JSON script missing. Ensure that the markup has <script ${dataAttr}...> included`);
    }
  
    return `${script.innerHTML}`.trim();
  }
  
  /**
   * Same as getValueOrFail but doesn't throw an exception when the value is not found, 
   * instead, will return the default value specified
   * 
   * @param {String} dataAttr 
   * @param {Any} defaultValue
   * @returns {String} String value of the target.
   */
  window.getValueOrDefault = (dataAttr, defaultValue) => {  
    const script = document.querySelector(`script[${dataAttr}]`);
    if (!script) {
      return defaultValue;
    }
  
    return `${script.innerHTML}`.trim();
  }
  
  /**
   * Given a <script type="application/json" data-indicator-json>...</script> in your markup, this function will 
   * take the contents of that script tag and return it as a regular json object. This is useful for getting data
   * from liquid objects.
   * 
   * Tip, use the appropriate MIME type: application/json (don't use text/json)
   * 
   * Example:
   * <script type="application/json" data-variant-json>{{ product.selected_or_first_available_variant | json }}</script>
   * ... in JS:
   * const variant = getScriptJSONOrFail("data-variant-json");
   * 
   * @param {String} dataAttr 
   * @returns {Object} JSON object
   */
  window.getScriptJSONOrFail = (dataAttr) => {
    try {
      return JSON.parse(getValueOrFail(dataAttr));
    } catch (e) {
      console.error(`Failed to parse JSON content of script ${dataAttr}`);
      throw e;
    }
  }
  
  /**
   * Formats a number based on the theme's money format.
   * This function requres the global variable `window.theme.moneyFormat` to be initialized.
   * 
   * @param {Number} num - The number to convert, passing a string will cause the function to fail.
   * @param {Number} showCents - Defaults to true, specify 'hide-cents' for more verbosity or any value other than 'show'
   * @returns {String} Money formatted number
   */
  window.toMoneyFormat = (num, showCents = 'show') => {  
    const decimals = showCents === 'show' || showCents === 'show-cents' ? 2 : 0;
    return `${window.theme.moneyFormat}`.replace("{{amount}}", num.toFixed(decimals))
  }
  
  /**
   * Creates a debounced function.
   * Debounced functions do not execute right away preventing multiple executions within
   * the timeout specified.
   * 
   * @param {Function} func - The function to debounce
   * @param {Number} timeout - How log, in milliseconds should the function debounce
   * @param {Object} context - Will be used to bind the function to.
   * @returns {Function} Higher order debouced function
   */
  window.debounce = (func, timeout, context = null) => {
    let timer;
    return (...args) => {
      const appliedContext = context ? context : this;
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(appliedContext, args), timeout);
    };
  }
  
  /**
   * Creates an object that contains the query parameters of the current
   * url. For example, if we had mystore.com/?q=gooddeals, this will output
   * an object that looks like:
   * {
   *   q: "gooddeals"
   * }
   * 
   * @returns {Object} The query parameters in object format
   */
  window.getQueryParams = () => {
    return new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop)
    });
  }